if (!customElements.get('slideshow-component')) {
  customElements.define(
    'slideshow-component',
    class SlideshowComponent extends HTMLElement {
      constructor() {
        super();

        this.sliderControls = this.querySelector('.swiper-controls');
        this.sliderInstance = false;
        this.layout = this.dataset.layout;
        this.items = Number(this.dataset.items);
        this.focusListeners = [];

        this.selectedIndex = this.selectedIndex; // eslint-disable-line no-self-assign -- triggers setter on custom element upgrade

        if (this.classList.contains('slideshow-height--adapt')) {
          this.calcSlideHeight();
        }

        this.initSlider();
      }

      disconnectedCallback() {
        // Remove all focus listeners added in initSlider()
        if (this.focusListeners && Array.isArray(this.focusListeners)) {
          this.focusListeners.forEach(({ element, handler }) => {
            if (element && typeof handler === 'function') {
              element.removeEventListener('focusin', handler);
            }
          });
          this.focusListeners = [];
        }
        // Cleanup the autoplay visibility observer set up in
        // setupAutoplayVisibilityObserver()
        if (this.autoplayVisibilityObserver) {
          this.autoplayVisibilityObserver.disconnect();
          this.autoplayVisibilityObserver = null;
        }
        // Cleanup slider
        if (this.sliderInstance && typeof this.sliderInstance === 'object') {
          this.sliderInstance.slider.destroy();
          this.sliderInstance = false;
        }
      }

      static get observedAttributes() {
        return ['selected-index'];
      }

      get selectedIndex() {
        return parseInt(this.getAttribute('selected-index', 10), 10) || 0;
      }

      set selectedIndex(index) {
        this.setAttribute('selected-index', `${index}`);
      }

      initSlider() {
        const additionModules = [FoxTheme.Swiper.Autoplay, FoxTheme.Swiper.EffectFade];

        // iOS detection - ALL iOS browsers use Safari's WebKit engine (Apple requirement)
        const isIOS = /iPhone|iPad|iPod/.test(navigator.userAgent);

        // Disable loop on all iOS devices: loop clones + fade effect cause transitionend
        // race conditions on iOS Safari's GPU compositor, producing rapid slide switching.
        // Without clones, the slideshow cycles cleanly via slideTo(0) on the last slide.
        const shouldDisableLoop = isIOS;

        this.sliderOptions = {
          slidesPerView: 1,
          spaceBetween: 10,
          loop: !shouldDisableLoop, // Disabled on all iOS to avoid fade+clone transitionend race
          grabCursor: !shouldDisableLoop,
          allowTouchMove: true,
          threshold: 2,
          speed: 600,
          effect: 'fade',
          on: {
            init: this.handleAfterInit.bind(this),
          },
          fadeEffect: {
            crossFade: true,
          },
        };

        if (this.sliderControls) {
          this.sliderOptions.pagination = {
            el: this.sliderControls.querySelector('.swiper-pagination'),
            clickable: true,
          };
        }

        switch (this.layout) {
          case 'centered':
            this.sliderOptions = {
              ...this.sliderOptions,
              centeredSlides: true,
              slidesPerView: 'auto',
              effect: 'slide',
            };
            break;
        }

        if (this.layout !== 'centered' && this.items === 1) {
          this.sliderOptions.loop = false;
          this.classList.remove('slideshow-controls-visible--yes');
        }

        const autoplayDelay = parseInt(this.dataset.autoplay, 10);
        if (autoplayDelay > 0) {
          this.sliderOptions = {
            ...this.sliderOptions,
            autoplay: {
              delay: autoplayDelay,
              disableOnInteraction: false,
              waitForTransition: true,
            },
          };
        }

        this.sliderInstance = new window.FoxTheme.Carousel(
          this,
          this.sliderOptions,
          additionModules
        );
        this.sliderInstance.init();

        this.sliderInstance.slider.on('realIndexChange', this.handleSlideChange.bind(this));

        let _lastTransitionWasClone = false;
        this.sliderInstance.slider.on('slideChangeTransitionStart', (swiper) => {
          if (FoxTheme.config.motionReduced) return;

          // Synchronously hide outgoing real-slide text and re-arm it for the next visit.
          // Without this, style.opacity='1' left by initialize() persists on inactive elements;
          // Swiper's cross-fade composites non-active text at full opacity for ~1 frame on Android.
          if (swiper.previousIndex !== undefined) {
            const outgoing = swiper.slides[swiper.previousIndex];
            if (outgoing && !outgoing.classList.contains('swiper-slide-duplicate')) {
              outgoing.querySelectorAll('motion-element[data-text]').forEach((el) => {
                el.getAnimations().forEach((a) => a.cancel());
                el.style.opacity = '0';
                if (el.animationType === 'fade-up') el.style.transform = 'translateY(2.5rem)';
                el._played = false;
                el.setAttribute('hold', '');
              });
            }
          }

          const incoming = swiper.slides[swiper.activeIndex];
          if (!incoming) return;

          if (incoming.classList.contains('swiper-slide-duplicate')) {
            // resetAnimation() at init leaves clone elements at opacity:0; sync from real slide so text/buttons appear during the 600ms fade-in before loopFix fires.
            const slideIndex = parseInt(incoming.dataset.swiperSlideIndex || '-1', 10);
            if (slideIndex >= 0) {
              const realSlides = Array.from(swiper.slides).filter(
                (s) => !s.classList.contains('swiper-slide-duplicate')
              );
              const realSlide = realSlides[slideIndex];
              if (realSlide) {
                const cloneEls = incoming.querySelectorAll('motion-element');
                const realEls = realSlide.querySelectorAll('motion-element');
                Array.from(cloneEls).forEach((cloneEl, i) => {
                  const realEl = realEls[i];
                  if (!realEl) return;
                  // Cancel stale WAAPI (e.g. from resetAnimation) so inline style takes effect in the same paint frame.
                  cloneEl.getAnimations().forEach((a) => a.cancel());
                  cloneEl.style.transition = 'none';
                  // If the real element was re-armed by outgoing-hide (hold + !_played), its
                  // opacity is '0' but the user is navigating TO this clone — show it fully visible.
                  if (
                    realEl.hasAttribute('data-text') &&
                    realEl.hasAttribute('hold') &&
                    !realEl._played
                  ) {
                    cloneEl.style.opacity = '1';
                    if (realEl.animationType === 'fade-up') {
                      cloneEl.style.transform = 'translateY(0)';
                    }
                    cloneEl._played = true;
                  } else {
                    if (realEl.style.opacity) cloneEl.style.opacity = realEl.style.opacity;
                    if (realEl.style.transform) cloneEl.style.transform = realEl.style.transform;
                    cloneEl._played = realEl._played;
                  }
                  requestAnimationFrame(() => cloneEl.style.removeProperty('transition'));
                });
              }
            }
            _lastTransitionWasClone = true;
            return;
          }

          // loopFix: clone showed text at opacity:1 during the 600ms crossFade; snap real slide to match instantly. Flag-based detection is reliable — getComputedStyle reads a stale 0 when Swiper sets opacity after firing the event.
          if (_lastTransitionWasClone) {
            _lastTransitionWasClone = false;
            incoming.querySelectorAll('motion-element[data-text]').forEach((el) => {
              el.getAnimations().forEach((a) => a.cancel());
              el._played = true;
              el.removeAttribute('hold');
              el.style.transition = 'none';
              el.style.opacity = '1';
              if (el.animationType === 'fade-up') el.style.transform = 'translateY(0)';
              requestAnimationFrame(() => el.style.removeProperty('transition'));
            });
            return;
          }
          // Defer ALL incoming text: resetAnimation() sets opacity:0 but not hold, so first-visit slides lack hold and the IO would fire initialize() during the crossFade (nested opacity → Android blink); set hold unconditionally to block it.
          incoming.querySelectorAll('motion-element[data-text]').forEach((el) => {
            el.getAnimations().forEach((a) => a.cancel());
            el._played = false;
            el.style.transition = 'none';
            el.style.opacity = '0';
            if (el.animationType === 'fade-up') el.style.transform = 'translateY(2.5rem)';
            el.setAttribute('hold', ''); // transitionEnd releases and animates
          });
          // Non-text elements have no opacity side-effect in preInitialize; run during crossFade only if explicitly held by outgoing-hide.
          incoming.querySelectorAll('motion-element:not([data-text])[hold]').forEach((el) => {
            el._played = false;
            el.removeAttribute('hold');
            el.preInitialize();
            el.initialize();
          });
        });

        this.sliderInstance.slider.on('slideChangeTransitionEnd', (swiper) => {
          if (FoxTheme.config.motionReduced) return;
          const active = swiper.slides[swiper.activeIndex];
          if (!active || active.classList.contains('swiper-slide-duplicate')) return;

          const held = Array.from(active.querySelectorAll('motion-element[data-text][hold]'));
          held.forEach((el, i) => {
            el.removeAttribute('hold');
            el._played = true;
            el.getAnimations().forEach((a) => a.cancel());
            const delayMs = i * 60;
            const keyframes =
              el.animationType === 'fade-up'
                ? [
                    { opacity: 0.01, transform: 'translateY(2.5rem)' },
                    { opacity: 1, transform: 'translateY(0)' },
                  ]
                : [{ opacity: 0.01 }, { opacity: 1 }];
            el.style.transition = 'none';
            el.animate(keyframes, {
              duration: 350,
              delay: delayMs,
              easing: 'cubic-bezier(0, 0, 0.3, 1)',
              fill: 'forwards',
            })
              .finished.then(() => {
                el.style.opacity = '1';
                if (el.animationType === 'fade-up') el.style.transform = 'translateY(0)';
                requestAnimationFrame(() => el.style.removeProperty('transition'));
              })
              .catch(() => {});
          });
        });

        if (this.sliderInstance) {
          const motionTextElements = Array.from(this.querySelectorAll('motion-element[data-text]'));
          motionTextElements &&
            motionTextElements.forEach((motionTextElement) => {
              if (
                typeof motionTextElement.resetAnimation === 'function' &&
                !motionTextElement.closest('.swiper-slide-active')
              ) {
                motionTextElement.resetAnimation();
              }
            });
          this.selectedElement =
            this.sliderInstance.slider.slides[this.sliderInstance.slider.activeIndex];
          this.onReady(this.selectedElement, this.sliderInstance.slider.slides);

          // Prevent page-load black flash on Android: preInitialize() left slide 0 text at opacity:0.01 — on Android's GPU compositor a near-zero opacity layer renders as solid black before WAAPI starts. Slides 2+ avoid this because resetAnimation() commits opacity:0 (fully transparent, no GPU layer promotion). Apply the same approach here: override to opacity:0, block IO via hold (must happen after onReady so refreshAnimation() doesn't re-run preInitialize()), then animate in via rAF using the same transitionEnd path.
          if (!FoxTheme.config.motionReduced && this.selectedElement) {
            Array.from(this.selectedElement.querySelectorAll('motion-element[data-text]')).forEach(
              (el) => {
                el.getAnimations().forEach((a) => a.cancel());
                el.style.transition = 'none';
                el.style.opacity = '0';
                if (el.animationType === 'fade-up') el.style.transform = 'translateY(2.5rem)';
                el._played = false;
                el.setAttribute('hold', '');
              }
            );
            requestAnimationFrame(() => {
              Array.from(
                this.selectedElement.querySelectorAll('motion-element[data-text][hold]')
              ).forEach((el, i) => {
                el._played = true;
                el.removeAttribute('hold');
                el.getAnimations().forEach((a) => a.cancel());
                const delayMs = parseInt(el.dataset.motionDelay || 0, 10) || i * 60;
                const keyframes =
                  el.animationType === 'fade-up'
                    ? [
                        { opacity: 0.01, transform: 'translateY(2.5rem)' },
                        { opacity: 1, transform: 'translateY(0)' },
                      ]
                    : [{ opacity: 0.01 }, { opacity: 1 }];
                el.style.transition = 'none';
                el.animate(keyframes, {
                  duration: 350,
                  delay: delayMs,
                  easing: 'cubic-bezier(0, 0, 0.3, 1)',
                  fill: 'forwards',
                })
                  .finished.then(() => {
                    el.style.opacity = '1';
                    if (el.animationType === 'fade-up') el.style.transform = 'translateY(0)';
                    requestAnimationFrame(() => el.style.removeProperty('transition'));
                  })
                  .catch(() => {});
              });
            });
          }

          // Fix accessibility
          const focusableElements = FoxTheme.a11y.getFocusableElements(this);
          if (this.layout === 'centered') {
            focusableElements.forEach((element) => {
              const handler = () => {
                const slide = element.closest('.swiper-slide');
                const slideToIndex = this.sliderInstance.slider.slides.indexOf(slide);
                this.sliderInstance.slider.slideToLoop(slideToIndex);
              };
              element.addEventListener('focusin', handler);
              this.focusListeners.push({ element, handler });
            });
          } else {
            focusableElements.forEach((element) => {
              const handler = () => {
                const slide = element.closest('.swiper-slide');
                this.sliderInstance.slider.slideTo(
                  this.sliderInstance.slider.slides.indexOf(slide)
                );
              };
              element.addEventListener('focusin', handler);
              this.focusListeners.push({ element, handler });
            });
          }

          this.setupAutoplayVisibilityObserver();
        }
      }

      // Nothing ever stopped autoplay while the slideshow was scrolled off
      // screen, so it kept firing a full slide transition every `delay` ms
      // regardless of visibility - each one running the querySelectorAll
      // sweeps, Element.animate() calls, and rAFs in
      // slideChangeTransitionStart/End above. The slideshow, its
      // transitions, and autoplay itself are unchanged; this only stops
      // autoplay from running when nobody can see it and restarts it when it
      // becomes visible again.
      setupAutoplayVisibilityObserver() {
        if (!('IntersectionObserver' in window)) return;

        this.autoplayVisibilityObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (!this.sliderInstance?.slider?.autoplay) return;
            // Swiper's autoplay.stop()/start() are safe to call regardless of
            // waitForTransition: stop() cancels whatever delay/transition
            // timer is pending rather than needing the in-flight transition
            // to finish first, and start() re-arms the delay timer from a
            // stopped state without requiring a matching prior stop().
            if (entry.isIntersecting) {
              this.sliderInstance.slider.autoplay.start();
            } else {
              this.sliderInstance.slider.autoplay.stop();
            }
          });
        });

        this.autoplayVisibilityObserver.observe(this);
      }

      onReady(selectedElement) {
        if (selectedElement.dataset.type === 'video') {
          const videoElement = FoxTheme.utils.displayedMedia(
            selectedElement.querySelectorAll('video-element')
          );
          videoElement && videoElement.play();
        }

        if (!FoxTheme.config.motionReduced) {
          const motionEls = selectedElement.querySelectorAll('motion-element');
          motionEls.forEach((motionEl) => {
            if (motionEl && motionEl.hasAttribute('hold')) motionEl.refreshAnimation();
          });
        }
      }

      handleAfterInit() {
        this.removeAttribute('data-media-loading');

        // Fix active bullet not transition on the first time.
        if (this.sliderControls) {
          const activeBullet = this.sliderControls.querySelector(
            '.swiper-pagination-bullet-active'
          );

          if (activeBullet) {
            activeBullet.classList.remove('swiper-pagination-bullet-active');
            activeBullet.offsetHeight; // Trigger reflow.
            activeBullet.classList.add('swiper-pagination-bullet-active');
          }
        }
      }

      handleSlideChange(swiper) {
        const { slides, realIndex, activeIndex } = swiper;
        this.selectedIndex = realIndex;

        this.updateControlsScheme(slides[activeIndex]);
      }

      attributeChangedCallback(name, oldValue, newValue) {
        if (name === 'selected-index' && oldValue !== null && oldValue !== newValue) {
          // Exclude Swiper loop clones (swiper-slide-duplicate) to avoid a race with loopFix().
          // In loop mode, Swiper first navigates to a clone, then silently repositions to the real
          // slide via loopFix(). Acting on the clone here would leave the real slide's text invisible.
          const isRealSlide = (el) => !el.classList.contains('swiper-slide-duplicate');

          // In non-loop mode (iOS), Swiper never assigns data-swiper-slide-index so the attribute
          // query returns empty. Fall back to DOM position which matches realIndex in non-loop mode.
          const allSlides = Array.from(this.querySelectorAll('.swiper-slide')).filter(isRealSlide);

          const fromDom = Array.from(
            this.querySelectorAll(`[data-swiper-slide-index="${oldValue}"]`)
          ).filter(isRealSlide);
          const fromElements = fromDom.length
            ? fromDom
            : allSlides[parseInt(oldValue, 10)]
              ? [allSlides[parseInt(oldValue, 10)]]
              : [];

          const toDom = Array.from(
            this.querySelectorAll(`[data-swiper-slide-index="${newValue}"]`)
          ).filter(isRealSlide);
          const toElements = toDom.length
            ? toDom
            : allSlides[parseInt(newValue, 10)]
              ? [allSlides[parseInt(newValue, 10)]]
              : [];

          fromElements.forEach((fromElement) => {
            if (fromElement.dataset.type === 'video') {
              const videoElement = FoxTheme.utils.displayedMedia([
                fromElement.querySelector('video-element'),
              ]);
              videoElement && videoElement.pause();
            }
          });

          toElements.forEach((toElement) => {
            setTimeout(() => {
              if (!toElement.isConnected) return;
              if (toElement.dataset.type === 'video') {
                const videoElement = FoxTheme.utils.displayedMedia([
                  toElement.querySelector('video-element'),
                ]);
                videoElement && videoElement.play();
              }
            }, 50);
          });
        }
      }

      updateControlsScheme(activeSlide) {
        if (this.sliderControls) {
          const classesToRemove = Array.from(this.sliderControls.classList).filter((className) =>
            className.startsWith('color-')
          );
          classesToRemove.forEach((className) => this.sliderControls.classList.remove(className));
          const colorScheme = activeSlide.dataset.colorScheme;
          this.sliderControls.classList.add(colorScheme);
        }
      }

      calcSlideHeight() {
        let maxHeight = 0;
        const slides = this.querySelectorAll('.swiper-slide');
        slides &&
          slides.forEach((slide) => {
            const slideText = slide.querySelector('.slideshow__text');
            if (slideText) {
              const slideTextHeight = slideText.offsetHeight;
              if (slideTextHeight > maxHeight) {
                maxHeight = slideTextHeight;
              }
            }
          });

        if (this.classList.contains('slideshow-controls-position--bottom')) {
          maxHeight += 100; // Avoid overlap with dots.
        }

        this.style.setProperty('--slide-height', maxHeight + 'px');
      }
    }
  );
}
